import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { type JSX, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";

import { BlogPostForm } from "@/components/blog/blog-post-form.tsx";
import { BlogPostFormSchema } from "@/components/blog/type/blog-post-form-schema";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { useGraphQL, useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";

const EditBlogPostContentPageQuery = graphql(/* GraphQL */ `
  query EditBlogPostContentPageQuery($userPostId: Int!) {
    membro {
      publicacoesBlog(where: { idUsuarioPosts: { eq: $userPostId } }) {
        nodes {
          conteudo
          dataCadastro
          publicado
          titulo
        }
      }
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const EditBlogPostContentPageMutation = graphql(`
  mutation EditBlogPostContentPageMutation(
    $userPostId: Int!
    $title: String!
    $content: String!
  ) {
    criarOuAlterarPublicacaoBlog(
      input: { idUsuarioPosts: $userPostId, titulo: $title, conteudo: $content }
    ) {
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
      publicacaoBlogGraphQLType {
        idUsuarioPosts
      }
    }
  }
`);

const TWO_SECONDS = 2000;

const EditBlogPostContentPage = (): JSX.Element => {
  const { userPostId } = Route.useParams();
  const queryResult = useGraphQL(
    EditBlogPostContentPageQuery,
    { userPostId },
    {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
    },
  );
  const { data } = queryResult;
  const post = data?.membro?.publicacoesBlog?.nodes?.[0];

  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    EditBlogPostContentPageMutation,
  );

  const [showSaving, setShowSaving] = useState(false);

  useEffect(() => {
    if (isPending) {
      setShowSaving(true);
      return undefined;
    }

    if (showSaving) {
      const timer = setTimeout(() => {
        setShowSaving(false);
      }, TWO_SECONDS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isPending, showSaving]);

  const onSubmit = (response: z.infer<typeof BlogPostFormSchema>): void => {
    if (!response.idUsuarioPosts) {
      return;
    }

    void mutateAsync({
      content: response.conteudo,
      title: response.titulo,
      userPostId: response.idUsuarioPosts,
    });
  };

  const saveRef = useRef<number | null>(null);

  const debouncedSave = (values: z.infer<typeof BlogPostFormSchema>) => {
    if (saveRef.current) {
      window.clearTimeout(saveRef.current);
    }

    saveRef.current = window.setTimeout(() => {
      onSubmit({
        conteudo: values.conteudo,
        titulo: values.titulo,
        idUsuarioPosts: userPostId,
      });
    }, 700);
  };

  useEffect(() => {
    return () => {
      if (saveRef.current) {
        window.clearTimeout(saveRef.current);
      }
    };
  }, []);

  const form = useForm<z.infer<typeof BlogPostFormSchema>>({
    resolver: zodResolver(BlogPostFormSchema),
    values: {
      idUsuarioPosts: userPostId,
      titulo: post?.titulo ?? "",
      conteudo: post?.conteudo ?? "",
    },
  });

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Alterar conteúdo" />
      </HeaderRoot>
      <AsyncDataWrapper {...queryResult}>
        {post ? (
          <BlogPostForm form={form} onChange={debouncedSave} isChanging={showSaving} />
        ) : null}
      </AsyncDataWrapper>
    </>
  );
};

export const Route = createFileRoute("/settings/blog/$userPostId/edit")({
  component: EditBlogPostContentPage,
  parseParams: (params) => ({
    userPostId: z.int().parse(Number(params.userPostId)),
  }),
});
